// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { type Match } from 'react-router-dom';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { putSessionUpdate } from 'actions/sessions';
import { getResource } from 'actions/resources/thunks';

import calculateRequestPath, { splitPathAndQueryString } from 'api/calculate-resource-request-path';

import Loader from 'components/loader';
import Wrapper from 'components/shared/wrapper';

import SessionEditForm from 'containers/sessions/edit-form';

import { type StateType as ReducerStateType } from 'reducers';
import type { EngagementsType } from 'types/engagements';
import { type ClientsType } from 'types/clients';
import { type SubjectsType } from 'types/subjects';
import type { SessionsType, SessionStatusType } from 'types/sessions';

import { getBusiness } from 'utils/user-auth';

import buildInitialValues, { buildSubmitValues } from './helpers';

//

const calculateRequestArgs = (sessionId: string) => ({
  resource: 'sessions',
  resourceId: sessionId,
  includes: ['tutoringEngagement', 'tutoringEngagement.subject', 'tutoringEngagement.client'],
  isTutor: true,
});

// ENHANCE

type PassedInPropsType = { match: Match };

type DispatchPropsType = {
  dispatchGetResource: typeof getResource,
  dispatchPutSessionUpdate: typeof putSessionUpdate,
};

const mapDispatchToProps: DispatchPropsType = {
  dispatchGetResource: getResource,
  dispatchPutSessionUpdate: putSessionUpdate,
};

type CalculatedPropsType = {
  engagements: EngagementsType,
  clients: ClientsType,
  sessions: SessionsType,
  subjects: SubjectsType,
  pageReady: boolean,
};

const mapStateToProps = (
  { resources: { tutoringEngagements, clients, sessions, subjects, meta } }: ReducerStateType,
  {
    match: {
      params: { sessionId },
    },
  }: PassedInPropsType,
): CalculatedPropsType => {
  const sessionIdNonNull: any = sessionId;
  const session = sessions[sessionIdNonNull];

  let initialValues;
  if (!isEmpty(session)) {
    const { attributes } = session;
    initialValues = buildInitialValues(attributes);
  }

  const requestPath = calculateRequestPath(calculateRequestArgs(sessionIdNonNull));
  const requestMeta = get(meta, splitPathAndQueryString(requestPath));
  const pageReady = !!requestMeta;

  const business = getBusiness();

  return {
    initialValues,
    clients,
    engagements: tutoringEngagements,
    sessions,
    subjects,
    pageReady,
    business,
  };
};

type PropsType = CalculatedPropsType & PassedInPropsType & DispatchPropsType;

class SessionsEdit extends Component<PropsType> {
  componentDidMount() {
    const {
      dispatchGetResource,
      match: {
        params: { sessionId },
      },
    } = this.props;
    if (sessionId) {
      dispatchGetResource(calculateRequestArgs(sessionId));
    }
  }

  handleEditFormSubmit = (
    sessionStatus: SessionStatusType,
    shouldUpdateFutureSessions: boolean,
  ) => async formValues => {
    const {
      dispatchPutSessionUpdate,
      match: {
        params: { sessionId },
      },
    } = this.props;
    const values = await buildSubmitValues(sessionStatus, shouldUpdateFutureSessions, formValues);
    if (sessionId) {
      dispatchPutSessionUpdate(sessionId, values);
    }
  };

  render() {
    const {
      pageReady,
      match: {
        params: { sessionId },
      },
    } = this.props;
    return (
      <Wrapper hasBackground>
        {pageReady ? (
          <SessionEditForm
            {...this.props}
            sessionId={sessionId}
            handleClick={this.handleEditFormSubmit}
          />
        ) : (
          <Loader />
        )}
      </Wrapper>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SessionsEdit);



// WEBPACK FOOTER //
// ./src/screens/sessions/edit/index.js